import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
  GoogleMap,
  Marker,
  OverlayView,
  OverlayViewF,
  OVERLAY_MOUSE_TARGET,
  DirectionsService,
  DirectionsRenderer,
  useJsApiLoader,
} from '@react-google-maps/api';
import io from 'socket.io-client';
import { GOOGLE_MAPS_TRAVEL_MODE } from '../../../constants/constants';
// Driver position marker — uses CarIcon in src/assets/svgs/UberCarIcon.js
import { CarIcon } from '../../../assets/svgs/UberCarIcon';
import styles from './index.module.css';

const ACTIVE_UBER_STATUSES = [
  'en_route_to_pickup',
  'arriving',
  'arrived',
  'arrived_at_pickup',
  'on_trip',
  'arrived_at_dropoff',
];

const DEFAULT_MAP_HEIGHT = '380px';

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
};

const ConnectingBadge = () => (
  <div className={styles.connectingBadge}>
    <Spin indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />} />
    <span>Waiting for driver location…</span>
  </div>
);

export const UberLiveMapInline = ({
  rideId,
  uberStatus,
  pickupAddress,
  destinationAddress,
  lastLocation,
  apiUrl,
  isCompleted,
  height,
}) => {
  const normalizedStatus = typeof uberStatus === 'string' ? uberStatus : '';
  const isActive = ACTIVE_UBER_STATUSES.includes(normalizedStatus);
  const isOnTrip = normalizedStatus === 'on_trip';

  const [driverState, setDriverState] = useState(() => {
    if (lastLocation?.latitude != null && lastLocation?.longitude != null) {
      return {
        lat: lastLocation.latitude,
        lng: lastLocation.longitude,
        bearing: lastLocation.bearing || 0,
      };
    }
    return null;
  });

  const [directions, setDirections] = useState(null);
  const [directionsFetched, setDirectionsFetched] = useState(false);
  const [markerCoords, setMarkerCoords] = useState({ pickup: null, destination: null });

  const mapRef = useRef(null);
  const socketRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const directionsRequestIdRef = useRef(0);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  useEffect(() => {
    if (!rideId || isCompleted) return;

    const baseUrl = apiUrl || window.location.origin;
    const socket = io(`${baseUrl}/uber-location`, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('subscribe_ride', { rideId });
    });

    socket.on('location_update', (data) => {
      if (data.rideId !== rideId) return;
      const lat = data.latitude ?? data.location?.latitude;
      const lng = data.longitude ?? data.location?.longitude;
      if (lat == null || lng == null) return;
      setDriverState({ lat, lng, bearing: data.bearing || 0 });
    });

    return () => {
      socket.emit('unsubscribe_ride', { rideId });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [rideId, apiUrl, isCompleted]);

  useEffect(() => {
    if (!isOnTrip || !driverState || !destinationAddress || !isLoaded) return;

    if (!directionsServiceRef.current) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
    }

    const requestId = ++directionsRequestIdRef.current;

    directionsServiceRef.current.route(
      {
        origin: new window.google.maps.LatLng(driverState.lat, driverState.lng),
        destination: destinationAddress,
        travelMode:
          window.google.maps.TravelMode[GOOGLE_MAPS_TRAVEL_MODE] ||
          window.google.maps.TravelMode.DRIVING,
      },
      (response, status) => {
        if (requestId !== directionsRequestIdRef.current) return;

        if (status === 'OK') {
          setDirections(response);
          setDirectionsFetched(true);
          const leg = response.routes?.[0]?.legs?.[0];
          if (leg) {
            setMarkerCoords((prev) => ({
              ...prev,
              destination: {
                lat: leg.end_location.lat(),
                lng: leg.end_location.lng(),
              },
            }));
          }
        } else {
          console.warn(`[UberLiveMapInline] Directions request failed: ${status}`);
        }
      }
    );
  }, [driverState, isOnTrip, destinationAddress, isLoaded]);

  useEffect(() => {
    if (driverState && mapRef.current) {
      mapRef.current.panTo({ lat: driverState.lat, lng: driverState.lng });
      if (mapRef.current.getZoom() < 14) {
        mapRef.current.setZoom(15);
      }
    }
  }, [driverState]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handleDirectionsCallback = useCallback((response) => {
    if (response?.status === 'OK' && !directionsFetched) {
      setDirections(response);
      setDirectionsFetched(true);

      const leg = response.routes?.[0]?.legs?.[0];
      if (leg) {
        setMarkerCoords({
          pickup: {
            lat: leg.start_location.lat(),
            lng: leg.start_location.lng(),
          },
          destination: {
            lat: leg.end_location.lat(),
            lng: leg.end_location.lng(),
          },
        });
      }

      if (!driverState && mapRef.current && response.routes?.[0]?.bounds) {
        mapRef.current.fitBounds(response.routes[0].bounds, 40);
      }
    } else if (response?.status && response.status !== 'OK') {
      console.warn(`[UberLiveMapInline] Static directions request failed: ${response.status}`);
    }
  }, [directionsFetched, driverState]);

  const mapContainerStyle = {
    width: '100%',
    height: height || DEFAULT_MAP_HEIGHT,
  };

  if (!isLoaded) {
    return (
      <div className={styles.mapLoadingContainer}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </div>
    );
  }

  const hasRoute = pickupAddress && destinationAddress;

  const markerSvg = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="11" fill="#1C452B"/>
      <circle cx="12" cy="12" r="5" fill="#E1F4D3"/>
    </svg>`
  );
  const circleMarkerIcon = {
    url: `data:image/svg+xml;charset=UTF-8,${markerSvg}`,
    scaledSize: new window.google.maps.Size(24, 24),
    anchor: new window.google.maps.Point(12, 12),
  };

  return (
    <div className={styles.mapWrapper}>
      {!isCompleted && !driverState && <ConnectingBadge />}

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={13}
        options={mapOptions}
        onLoad={onMapLoad}
      >
        {hasRoute && !directionsFetched && !isOnTrip && (
          <DirectionsService
            options={{
              origin: pickupAddress,
              destination: destinationAddress,
              travelMode: GOOGLE_MAPS_TRAVEL_MODE,
            }}
            callback={handleDirectionsCallback}
          />
        )}

        {directions && (
          <DirectionsRenderer
            options={{
              directions,
              suppressMarkers: true,
              preserveViewport: true,
              polylineOptions: {
                strokeColor: isCompleted ? '#1a73e8' : '#315E45',
                strokeWeight: 4,
                strokeOpacity: 1,
              },
            }}
          />
        )}

        {markerCoords.pickup && !isOnTrip && (
          <Marker position={markerCoords.pickup} icon={circleMarkerIcon} zIndex={1} />
        )}
        {markerCoords.pickup && !isOnTrip && (
          <OverlayView
            position={markerCoords.pickup}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div className={styles.overlayLabelRoot}>
              <div className={styles.labelCard}>
                <div className={styles.labelTitle}>Pickup</div>
                <div className={styles.labelAddress}>{pickupAddress}</div>
              </div>
            </div>
          </OverlayView>
        )}

        {markerCoords.destination && (
          <Marker position={markerCoords.destination} icon={circleMarkerIcon} zIndex={1} />
        )}
        {markerCoords.destination && (
          <OverlayView
            position={markerCoords.destination}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div className={styles.overlayLabelRoot}>
              <div className={styles.labelCard}>
                <div className={styles.labelTitle}>Destination</div>
                <div className={styles.labelAddress}>{destinationAddress}</div>
              </div>
            </div>
          </OverlayView>
        )}

        {driverState && !isCompleted && (
          <OverlayViewF
            position={{ lat: driverState.lat, lng: driverState.lng }}
            mapPaneName={OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(w, h) => ({
              x: -w / 2,
              y: -h / 2,
            })}
          >
            <div
              className={styles.uberMapCarWrap}
              style={{
                transform: `rotate(${(Number(driverState.bearing) || 0)}deg)`,
              }}
              title="Driver"
            >
              <div className={styles.uberMapCarMap}>
                <CarIcon
                  width="100%"
                  height="100%"
                  className={styles.uberMapCarSvg}
                  aria-hidden
                />
              </div>
            </div>
          </OverlayViewF>
        )}
      </GoogleMap>
    </div>
  );
};
